import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import './i18n'
import './index.css'
import App from './App.jsx'
import { AuthProvider } from './auth.jsx'
import { captureError } from './sentry.js'

/**
 * Обработчики ошибок корня.
 *
 * ЗАЧЕМ. 05.10.2026 пришла жалоба: страница меняется в адресной строке, но не
 * отрисовывается до перезагрузки — пустой экран в цвет фона. В консоли было
 * «l is not a function» со стеком из минифицированного React: Cl/Yu/Ju. По
 * нему видно, ЧТО произошло (какой-то useEffect вернул не функцию, и React
 * попытался вызвать это как очистку), но не видно ГДЕ.
 *
 * Воспроизвести не удалось ни в Chromium, ни в WebKit, ни при переключении
 * темы и языка, ни при уходе со страниц с картой и видео. Границей ошибок это
 * тоже не ловится: сбой происходит в фазе удаления поддерева, и React сносит
 * весь корень — отсюда и пустой экран вместо сообщения.
 *
 * Поэтому здесь не «починка», а инструмент: при следующем случае в консоли
 * окажется стек КОМПОНЕНТОВ с человеческими именами. Этого достаточно, чтобы
 * найти виновника с первого раза, вместо того чтобы гадать по минифицированным
 * буквам.
 */
const RECOVER_KEY = 'urbanpurr.rootRecover'

const report = (kind) => (error, info) => {
  // eslint-disable-next-line no-console
  console.error(
    `UrbanPurr [${kind}]:`, error,
    '\nкомпоненты:', info?.componentStack || '(стек компонентов не пришёл)',
  )
  captureError(error, info, kind) // в Sentry, если задан VITE_SENTRY_DSN
  if (kind !== 'не поймано') return

  // Пока причина не найдена — не оставлять человека с пустым экраном.
  //
  // Корень уже снесён, и вернуть его на место нечем: перерисовывать нечего,
  // состояние потеряно. Единственное, что реально помогает, — ровно то, что
  // пользователь делает руками, то есть перезагрузка. Делаем её сами.
  //
  // РОВНО ОДИН РАЗ за вкладку: если причина не в случайности, а в самой
  // странице, вторая попытка упадёт так же, и повтор превратится в
  // бесконечный цикл — это хуже пустого экрана.
  setTimeout(() => {
    const root = document.getElementById('root')
    if (root && root.children.length > 0) return // дерево уцелело, всё в порядке
    try {
      if (sessionStorage.getItem(RECOVER_KEY) === '1') return
      sessionStorage.setItem(RECOVER_KEY, '1')
    } catch {
      return // приватный режим: лучше показать пустоту, чем зациклить вкладку
    }
    window.location.reload()
  }, 100)
}

createRoot(document.getElementById('root'), {
  onUncaughtError: report('не поймано'),
  onCaughtError: report('поймано границей'),
  onRecoverableError: report('восстановимое'),
}).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
